"use client";

import React, { useEffect, useState } from "react";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopSubmenu({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="flex min-w-[220px] flex-col gap-1 p-2 bg-popover text-popover-foreground rounded-md border border-border shadow-md">
      {items.map((item, i) => <li key={item.id || i} data-index={i} className="w-full">
          {item.children && item.children.length > 0 ? <div className="flex flex-col gap-1">
              <span className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {item.label}
              </span>
              <DesktopSubmenu items={item.children} />
            </div> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="block rounded-sm px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted hover:text-primary">
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}
function DesktopNavigation() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-24 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id || i} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="h-9 px-3 text-sm font-medium text-foreground hover:text-primary focus:text-primary data-[state=open]:text-primary">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <DesktopSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : item.label === "Request Service" ? <Button asChild size="default" className="h-10 px-4 font-bold tracking-tight shadow-xs">
                <Link to={item.href || "#"}>{item.label}</Link>
              </Button> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted hover:text-primary focus:bg-muted focus:text-primary">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileSubmenu({
  items,
  onNavigate,
  depth = 1
}: {
  items: MenuItem[];
  onNavigate: () => void;
  depth?: number;
}) {
  return <ul className={`flex flex-col gap-1 ${depth === 1 ? "pt-1" : "pl-4"}`}>
      {items.map((item, i) => <li key={item.id || i} data-index={i} className="flex flex-col">
          {item.children && item.children.length > 0 ? <Collapsible className="w-full">
              <CollapsibleTrigger className="flex w-full items-center justify-between py-2 px-3 text-left text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors">
                <span>{item.label}</span>
                <ChevronDown className="size-4 text-muted-foreground transition-transform duration-200" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <MobileSubmenu items={item.children} onNavigate={onNavigate} depth={depth + 1} />
              </CollapsibleContent>
            </Collapsible> : <Link to={item.href || "#"} onClick={onNavigate} className="block rounded-md px-3 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors">
              {item.label}
            </Link>}
        </li>)}
    </ul>;
}
function MobileNavigation({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2 p-4">
        <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <nav aria-label="Mobile Navigation" className="flex flex-col gap-1 p-4">
      {menuItems.map((item, i) => <div key={item.id || i} data-index={i} className="flex flex-col">
          {item.children && item.children.length > 0 ? <Collapsible className="w-full">
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-base font-medium text-foreground hover:bg-muted transition-colors">
                <span>{item.label}</span>
                <ChevronDown className="size-4 text-muted-foreground transition-transform duration-200" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-2">
                <MobileSubmenu items={item.children} onNavigate={onNavigate} />
              </CollapsibleContent>
            </Collapsible> : item.label === "Request Service" ? <Button asChild className="w-full h-11 text-base font-bold">
              <Link to={item.href || "#"} onClick={onNavigate}>
                {item.label}
              </Link>
            </Button> : <Link to={item.href || "#"} onClick={onNavigate} className="rounded-md px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted hover:text-primary transition-colors">
              {item.label}
            </Link>}
        </div>)}
    </nav>;
}
export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 80;
            const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className={`sticky top-0 z-50 w-full transition-shadow duration-200 ${scrolled ? "bg-background/95 backdrop-blur-md shadow-md border-b border-border" : "bg-background border-b border-border"}`}>
      <header className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Left: Sole Brand Logo */}
        <div className="flex shrink-0 items-center">
          <Link to="/" aria-label="TriServe homepage">
            <WvcLogo className="h-12 w-auto md:h-14" />
          </Link>
        </div>

        {/* Center: Primary Navigation */}
        <div className="hidden lg:flex lg:items-center lg:justify-center">
          <MenuProvider menu_id="31">
            <DesktopNavigation />
          </MenuProvider>
        </div>

        {/* Right: Emergency Dispatch Phone & Mobile Hamburger */}
        <div className="flex items-center gap-3">
          <Button asChild variant="default" size="default" className="h-10 px-3 sm:px-4 font-bold tracking-tight shadow-xs">
            <a href="tel:855-435-6631" className="inline-flex items-center gap-2" aria-label="Call 24/7 Dispatch 855-435-6631">
              <Phone className="size-4 shrink-0" aria-hidden="true" />
              <span className="font-mono text-sm sm:text-base whitespace-nowrap">
                855-435-6631
              </span>
            </a>
          </Button>

          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(prev => !prev)} className="lg:hidden" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen}>
            {mobileOpen ? <X className="size-6 text-foreground" /> : <Menu className="size-6 text-foreground" />}
          </Button>
        </div>
      </header>

      {/* Mobile Slide-down Panel */}
      {mobileOpen && <div className="border-b border-border bg-background shadow-lg lg:hidden">
          <MenuProvider menu_id="31">
            <MobileNavigation onNavigate={() => setMobileOpen(false)} />
          </MenuProvider>
          <div className="border-t border-border p-4">
            <Button asChild className="w-full h-11 text-base font-bold">
              <a href="tel:855-435-6631" className="flex items-center justify-center gap-2">
                <Phone className="size-4" />
                <span className="font-mono">855-435-6631</span>
              </a>
            </Button>
          </div>
        </div>}
    </section>;
}